/**
 * Shared criteria counting logic.
 *
 * Extracts the duplicated counting code from ServerLeaderboardService and
 * ServerPerformanceHistoryService into a single reusable function.
 */

/** Legacy metric type aliases used by Gen Korean BBQ format */
const LEGACY_METRIC_ALIASES: Record<string, string[]> = {
  metric_1: ['upsell_premium_menu', 'metric_1'],
  metric_2: ['mention_specific_premium_item', 'metric_2'],
  metric_3: ['describe_specific_premium_item', 'metric_3'],
};

export interface CriteriaCounts {
  /** Per-metric counts keyed by metric key (e.g. metric_1, metric_2, ...) */
  metricCounts: Record<string, number>;
  drinkPromotionCount: number;
  initialGreetingCount: number;
}

/**
 * Count fulfilled criteria from a list of shift interaction records.
 *
 * @param criteriaItems  Array of interaction records (with interaction_type, interaction_data)
 * @param metricKeys     Which metric keys to count (default: metric_1, metric_2, metric_3)
 * @returns Counts for each metric, drink promotions, and greetings
 */
export function calculateCriteriaCounts(
  criteriaItems: any[],
  metricKeys: string[] = ['metric_1', 'metric_2', 'metric_3']
): CriteriaCounts {
  const metricCounts: Record<string, number> = {};
  for (const key of metricKeys) {
    metricCounts[key] = 0;
  }
  let drinkPromotionCount = 0;
  let initialGreetingCount = 0;

  for (const criteriaItem of criteriaItems) {
    try {
      // Every shift interaction counts as a greeting
      initialGreetingCount++;

      // Count drink promotions
      if (criteriaItem.interaction_type === 'drink_promotion') {
        drinkPromotionCount++;
      }

      // The tenant's prompt config decides which interaction_types carry
      // service_criteria (via metrics[].applies_to). We don't gate counting on
      // a hardcoded interaction_type list — if the interaction has a
      // service_criteria array, parse it. Interaction types without
      // service_criteria (greetings, payment, goodbye, etc.) naturally fall
      // through the `data?.service_criteria` check below.
      const data: any = criteriaItem.interaction_data;
      if (!data?.service_criteria) continue;

      const criteriaArray = data.service_criteria;
      if (!Array.isArray(criteriaArray)) continue;

      for (const mk of metricKeys) {
        // Get legacy aliases for this key, or just use the key itself
        const aliases = LEGACY_METRIC_ALIASES[mk] || [mk];
        const found = criteriaArray.some(
          (c: any) => aliases.includes(c.type) && c.fulfilled === true
        );
        if (found) {
          metricCounts[mk]++;
        }
      }
    } catch (e) {
      console.error('Error processing interaction data:', e);
    }
  }

  return { metricCounts, drinkPromotionCount, initialGreetingCount };
}
